/**
 * @fileoverview Basics Step Component - Step 0 of case ingestion (matter creation).
 *
 * Collects the required fields to create a new matter:
 * - Firm selection (from available firms)
 * - Client first and last name (becomes matter name)
 * - Case type (required)
 *
 * On "Create Matter", the matter is created with CASE_INGESTION status.
 * Duplicate detection is handled by UnifiedFlowContext (owns the hook).
 */
import React from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue } from 'react-hook-form';

import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import {
  Combobox,
  ComboboxLabel,
  ComboboxOption,
} from '@/components/ui/catalyst/combobox';
import {
  Description,
  ErrorMessage,
  Field,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import {
  Listbox,
  ListboxLabel,
  ListboxOption,
} from '@/components/ui/catalyst/listbox';
import { useGetFirmsQuery } from '@/services/api/firmsService';
import { MatterType } from '@/services/types/client-intake-types';

import { GATE_TASKS_DEFAULT } from '../../schemas';
import type { CreateMatterFormValues } from '../../types';

interface BasicsStepProps {
  /** React Hook Form control */
  control: Control<CreateMatterFormValues>;
  /** Form validation errors */
  errors: FieldErrors<CreateMatterFormValues>;
  /** Currently selected firm */
  selectedFirm: { id: string; name: string } | null;
  /** Callback to update selected firm */
  setSelectedFirm: (firm: { id: string; name: string } | null) => void;
  /** React Hook Form setValue function */
  setValue: UseFormSetValue<CreateMatterFormValues>;
  /** Whether form is in loading state (creating matter) */
  isLoading: boolean;
  /** Whether the matter has already been created (firm should be read-only) */
  matterExists?: boolean;
}

/** Case type options derived from MatterType enum, excluding Unknown. */
const typeOptions = Object.values(MatterType)
  .filter((type) => type !== MatterType.UNKNOWN)
  .map((type) => ({
    value: type,
    label: type
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase()),
  }));

/**
 * Form fields for matter creation: firm selection, client name, and case type.
 */

export const BasicsStep: React.FC<BasicsStepProps> = ({
  control,
  errors,
  selectedFirm,
  setSelectedFirm,
  setValue,
  isLoading,
  matterExists = false,
}) => {
  const { data: firms, isLoading: firmsLoading } = useGetFirmsQuery({});

  const firmOptions =
    firms?.map((firm) => ({
      id: firm.id,
      name: firm.name,
    })) || [];

  const handleFirmChange = (firm: { id: string; name: string } | null) => {
    setSelectedFirm(firm);
    setValue('firm', firm || { id: '', name: '' }, { shouldValidate: true });
  };

  return (
    <div className="space-y-6">
      {/* Firm */}
      <Field>
        <Label>
          Firm <span className="text-red-500">*</span>
        </Label>
        <Combobox
          value={selectedFirm}
          onChange={handleFirmChange}
          options={firmOptions}
          displayValue={(firm) => firm?.name}
          placeholder="Select a firm"
          className="w-full"
          aria-label="Select a firm"
          disabled={firmsLoading || isLoading || matterExists}
        >
          {(option) => (
            <ComboboxOption value={option}>
              <ComboboxLabel>{option.name}</ComboboxLabel>
            </ComboboxOption>
          )}
        </Combobox>
        {errors.firm?.id?.message && (
          <ErrorMessage>{errors.firm.id.message}</ErrorMessage>
        )}
      </Field>

      {/* Client Name Fields (becomes the matter name) */}
      <div className="grid grid-cols-2 gap-4">
        <Field>
          <Label htmlFor="first_name">
            First Name <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="first_name"
            control={control}
            render={({ field }) => (
              <Input
                id="first_name"
                type="text"
                data-slot="control"
                disabled={isLoading}
                {...field}
              />
            )}
          />
          {errors.first_name?.message && (
            <ErrorMessage>{errors.first_name.message}</ErrorMessage>
          )}
        </Field>
        <Field>
          <Label htmlFor="last_name">
            Last Name <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="last_name"
            control={control}
            render={({ field }) => (
              <Input
                id="last_name"
                type="text"
                data-slot="control"
                disabled={isLoading}
                {...field}
              />
            )}
          />
          {errors.last_name?.message && (
            <ErrorMessage>{errors.last_name.message}</ErrorMessage>
          )}
        </Field>
      </div>

      {/* Case Type */}
      <Field>
        <Label htmlFor="matter_type">
          Case Type <span className="text-red-500">*</span>
        </Label>
        <Controller
          name="matter_type"
          control={control}
          render={({ field }) => (
            <Listbox
              disabled={isLoading}
              value={field.value ?? null}
              onChange={field.onChange}
              placeholder="Select case type"
            >
              {typeOptions.map((option) => (
                <ListboxOption key={option.value} value={option.value}>
                  <ListboxLabel>{option.label}</ListboxLabel>
                </ListboxOption>
              ))}
            </Listbox>
          )}
        />
        {errors.matter_type?.message && (
          <ErrorMessage>{errors.matter_type.message}</ErrorMessage>
        )}
      </Field>

      {/* Task Gating Option */}
      <Controller
        name="gate_tasks_during_initial_review"
        control={control}
        render={({ field }) => (
          <CheckboxField>
            <Checkbox
              checked={field.value ?? GATE_TASKS_DEFAULT}
              onChange={field.onChange}
              disabled={isLoading}
              color="indigo"
            />
            <Label>Hide tasks during initial review</Label>
            <Description>
              Hides all tasks except Retainer, HIPAA, and Initial Review until
              initial review is complete
            </Description>
          </CheckboxField>
        )}
      />
    </div>
  );
};
